import React, { useEffect, useState } from 'react';

import { Container } from '@material-ui/core';
import APIError from 'components/AlertScreens/APIError';
import ListSkeletonLoader from 'components/AlertScreens/ListSkeletonLoader';
import NoQuestionsScreen from 'components/Templates/NoQuestionsScreen';
import useAsync from 'components/hooks/useAsync';
import { TemplateQuestion } from 'models/templates/review';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { StyledBox, StyledPaper } from 'styles/common';

import { QUESTION_ORDER_ACTIONS, REVIEW_QUESTION_TYPES, REVIEW_QUESTION_TYPE_MAP_2 } from 'constants/ProjectConstants';
import { swapElementsInArray } from 'constants/helper';
import theme from 'constants/theme';

import { showInfoSnackbar } from 'services/snackbar';
import { getReviewTemplateQuestions } from 'services/templates/review';

import { Typography10, Typography11 } from 'uiToolkit/Typography';

import { useQuestionListSidePanel } from '../../../common/TemplateCommons/QuestionListSidepanel/context/QuestionListSidepanelContext';
import { useReviewTemplate } from '../common/ReviewTemplateContext';
import { ContentContainer } from '../common/styles';
import QuestionRowType2 from './common/QuestionRowType2';

const OverAllQuestions = () => {
  const [selectedId, setSelectedId] = useState();

  const { showSidepanel } = useQuestionListSidePanel();
  const {
    state: { META, QNA },
    setQuestions,
    addQuestion,
    removeQuestion,
  } = useReviewTemplate();

  const { templateQuestions } = QNA;

  const { data, run, isLoading, isError } = useAsync();
  const dispatch = useDispatch();

  const getData = () => {
    run(getReviewTemplateQuestions({ templateId: META.id, section: REVIEW_QUESTION_TYPES.QNA }));
  };

  useEffect(() => {
    if (META.id) {
      getData();
    }
  }, [META]);

  useEffect(() => {
    data && setQuestions({ section: REVIEW_QUESTION_TYPES.QNA, questions: data.qnaQuestions });
  }, [data]);

  const onQuestionAddition = ({ question, insertionPosition }) => {
    const templateQuestion = TemplateQuestion.fromJSON({
      templateId: META.id,
      question,
    });
    addQuestion({
      question: templateQuestion,
      section: REVIEW_QUESTION_TYPES.QNA,
      insertionPosition,
    });
  };

  const handleQuestionSelect = (id) => {
    setSelectedId(id);
  };

  const handleAddQuestion = ({ insertionPosition }) => {
    showSidepanel({
      open: true,
      onSuccess: onQuestionAddition,
      // Questions can be of ["GOAL", "COMPETENCY", "QNA"] and we need to show only of QNA aka Overall Questions type for sidepanel opened from Overall Questions Review.
      type: REVIEW_QUESTION_TYPE_MAP_2.QNA,
      insertionPosition,
    });
  };

  const handleRemoveQuestion = ({ index }) => {
    removeQuestion({
      section: REVIEW_QUESTION_TYPES.QNA,
      index,
    });
  };

  const handleQuestionDataChange = ({ param, value, index }) => {
    if (param === 'weightage' && (Number.isNaN(value) || value < 0 || value > 100)) {
      dispatch(showInfoSnackbar('Valid Weight Range is [0,100]'));
      return;
    }

    const newTemplateQuestion = {
      ...templateQuestions[index],
      [param]: value,
    };
    const updatedQuestions = [...templateQuestions];
    updatedQuestions[index] = newTemplateQuestion;
    setQuestions({ section: REVIEW_QUESTION_TYPES.QNA, questions: updatedQuestions });
  };

  const handleQuestionOrderChange = ({ index, action }) => {
    let newQuestionList = [];

    if (action === QUESTION_ORDER_ACTIONS.MOVE_UP) {
      newQuestionList = swapElementsInArray(templateQuestions, index - 1, index);
    } else {
      newQuestionList = swapElementsInArray(templateQuestions, index + 1, index);
    }

    // Map to maintain the displayOrder property
    newQuestionList = newQuestionList.map((item, idx) => ({
      ...item,
      displayOrder: idx + 1,
    }));
    setQuestions({ section: REVIEW_QUESTION_TYPES.QNA, questions: newQuestionList });
  };

  const getTotalWeightage = () => {
    let weight = 0;
    QNA.templateQuestions.forEach((item) => {
      weight += item.question.analyticsQuestionType === 'RATING' ? Number(item.weightage) || 0 : 0;
    });
    return weight.toFixed(2);
  };

  const totalWeight = getTotalWeightage();
  const maxWeightFixed = Number(100).toFixed(2);
  const minWeightFixed = Number(0).toFixed(2);

  return (
    <Container maxWidth="md">
      <ContentContainer transparent>
        <Typography11>Questions</Typography11>
      </ContentContainer>
      <ContentContainer transparent>
        {isError ? (
          <APIError reloadHandler={() => getData()} />
        ) : isLoading ? (
          <ListSkeletonLoader />
        ) : (
          <>
            {templateQuestions.length ? (
              templateQuestions.map((item, idx) => (
                <QuestionRowType2
                  key={item.id || idx}
                  addQuestionHandler={handleAddQuestion}
                  questionObject={item}
                  handleRemoveQuestion={handleRemoveQuestion}
                  handleQuestionDataChange={handleQuestionDataChange}
                  questionOrderChangeHandler={handleQuestionOrderChange}
                  isFirstItem={idx === 0}
                  isLastItem={idx + 1 === templateQuestions.length}
                  index={idx}
                  handleQuestionSelect={handleQuestionSelect}
                  selectedId={selectedId}
                />
              ))
            ) : (
              <StyledBox paddingRight="10px">
                <NoQuestionsScreen addQuestionHandler={handleAddQuestion} />
              </StyledBox>
            )}
            <TotalWeightageContainer
              padding="1rem"
              variant="outlined"
              flexcontainer
              justifycontent="space-between"
              bgColor={theme.palette.background.lightGrey4}
            >
              <Typography10 style={{ fontSize: '14px' }} color="grey" weight="bold">
                Total weightage
              </Typography10>
              <Typography10
                style={{ fontSize: '24px' }}
                weight="bold"
                color={totalWeight === maxWeightFixed || totalWeight === minWeightFixed ? 'green' : 'red'}
                variant="h6"
              >
                {totalWeight}%
              </Typography10>
            </TotalWeightageContainer>
          </>
        )}
      </ContentContainer>
    </Container>
  );
};

export default OverAllQuestions;

const TotalWeightageContainer = styled(StyledPaper)`
  margin-right: 10px;
`;
